import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import useOutsideClick from 'components/hooks/useOutsideClick';
import usePrevious from 'components/hooks/usePrevious';

import OtherOptions from 'components/OtherOptions';

export default function Options({
  additional,
  align,
  btnToggle,
  buttonDisabled,
  children,
  darkHover,
  listStyle,
  onClose,
  onOpen,
  toggleStyle,
}) {
  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const prevIsOpen = usePrevious(isOpen);

  useOutsideClick(() => {
    setIsOpen(false);
  }, [ref]);

  const handleOptionClick = (optionCallback = () => {}, _child) => {
    optionCallback(setIsOpen);
  };

  const handleToggle = () => {
    if (!buttonDisabled) setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (prevIsOpen && !isOpen && onClose) onClose();
    if (!prevIsOpen && isOpen && onOpen) onOpen();
  }, [prevIsOpen, isOpen]);

  const handleTopLevelOptionClick = (event) => event.stopPropagation();

  const optionsAlignment = (() => {
    switch (align) {
      case 'left':
        return 'tw-justify-start';
      case 'center':
        return 'tw-justify-center';
      default:
        return 'tw-justify-end';
    }
  })();

  const optionsListAlignment = (() => {
    switch (align) {
      case 'left':
        return 'tw-left-0';
      case 'center':
        return 'tw-left-1/2 -tw-translate-x-1/2';
      default:
        return 'tw-right-0';
    }
  })();

  return (
    <div
      className={classNames('options', optionsAlignment, toggleStyle)}
      onClick={handleTopLevelOptionClick}
      ref={ref}
    >
      <div
        className={classNames(
          'options__toggle',
          btnToggle ?
            'options__toggle--btn tw-border-transparent tw-bg-transparent'
          : 'tw-border-white tw-bg-white group-hover/row:tw-border-grey-100',
          btnToggle && buttonDisabled && 'options__toggle--btn-disabled',
          isOpen && 'options--open',
          darkHover && 'options__toggle--dark-hover',
        )}
        onClick={handleToggle}
      >
        {btnToggle ?
          <>{btnToggle(isOpen)}</>
        : <OtherOptions blink={false} />}
      </div>
      {isOpen && (
        <ul
          className={classNames(
            'options__list tw-border-grey-100 tw-bg-white tw-shadow-sm',
            optionsListAlignment,
            listStyle,
          )}
        >
          {React.Children.toArray(children)
            .filter(Boolean)
            .map((child, index) => (
              <li
                className={classNames(
                  'options__choice tw-border-grey-100',
                  child.props.hoverClassName || 'hover:tw-bg-blue-025',
                  child.props.disabled ?
                    'tw-cursor-not-allowed'
                  : 'tw-cursor-pointer',
                )}
                key={index}
                onClick={() => {
                  if (!child.props.disabled) {
                    handleOptionClick(child.props.onClick, child);
                  }
                }}
              >
                <div
                  className={classNames(
                    child.props.disabled && 'tw-opacity-30',
                  )}
                >
                  {child}
                </div>
              </li>
            ))}
        </ul>
      )}

      {isOpen && additional && (
        <div className='options__additional'>{additional}</div>
      )}
    </div>
  );
}
